import { Box, Checkbox, FormControlLabel, Grid, TextField } from "@mui/material";
import * as React from "react";
import { CheckboxDropdown } from "ui/Scripts/source/components/framework/Dropdown/CheckboxDropdown";
import { TreeCheckboxDropdown } from "ui/Scripts/source/components/framework/Dropdown/TreeCheckboxDropdown";
import { Base } from "ui/Scripts/source/framework/base";
import { IIdTitle, IdTitle } from "ui/Scripts/source/models/common/idTitle";
import { ApplicationPermissionType, ConfirmationDialogResult, EventStateFilter, FrontPagePanelSize, ReportType } from "ui/Scripts/source/models/enums";
import { ITaskDataRow, TaskDataRow } from "ui/Scripts/source/models/task/taskDataRow";
import { ITaskDataRowCol } from "ui/Scripts/source/models/task/taskDataRowCol";
import { ITaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "source/framework/appUtils";
import { SaveDataObj } from "source/framework/saveData";
import { IChartItem } from "source/models/chart/chartItem";
import { ISavedChartPanel, SavedChartPanel } from "source/models/chart/savedChartPanel";
import { IReportItem, ReportItem } from "source/models/report/reportItem";
import * as chartPanelService from "source/models/services/chartPanelService";
import { FilterButtons } from "source/components/framework/Filters/FilterButtons";
import { FilterSearchField } from "source/components/framework/Filters/FilterSearchField";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { TaskDynamicForm } from "source/components/task/TaskDynamicForm/TaskDynamicForm";
import { FormSectionTitle } from "source/components/framework/Form/FormSectionTitle";
import { DatePicker } from "source/components/framework/DateTime/DatePicker";
import { ISaveReportFilters, SaveReportDialog } from "source/components/report/PageNavMenuItemReportList/SaveReportDialog";
import { cloneEventPageFilterState, EventTypeNotSetId, getEventGeneralFilterFromFilterState, IEventGeneralFilter, IEventPageFilterLists, IEventPageFilterState, IEventPageFilterStateWithoutDates } from "./EventPageUtils";
import { IdTitleDropdown } from "ui/Scripts/source/components/framework/Dropdown/IdTitleDropdown";
import { PageChartFilterDrawer } from "source/components/framework/Page/PageChartFilterDrawer";

export interface IEventPageReportFilterPanelProp extends IEventPageFilterLists, IEventPageFilterState {
    classes?: string;
    chartPanelDrawerMode?: boolean;
    chartPanel?: ISavedChartPanel;
    chartItems?: IChartItem[];
    reservedChartNames?: string[];
    report: IReportItem;
    panelSize?: number;
    panelVisible?: boolean;
    canRemoveSharedReports: boolean;
    onSaveReport: (saveAs: boolean, onGetReportSaveFilters: () => ISaveReportFilters) => void;
    onRemoveReport: (filterId: string) => void;
    onRemoveChart?: (chartId: string) => void;
    onOk: (filterState: IEventPageFilterState, parameters: ITaskDataRow) => ISaveReportFilters;
    onChartPanelSaved?: (chartPanel: ISavedChartPanel) => void;
    onChartPanelRemoved?: (chartPanelId: string) => void;
    onCancel: () => void;
}

export interface IEventPageReportFilterPanelState extends IEventPageFilterStateWithoutDates {
    dynamicTimeFilters: IIdTitle[];
    taskType: ITaskEditTypeItem; //custom parameters schema
    taskDataRow: ITaskDataRow; //custom parameter values
    name: string;
    charts: IIdTitle[];
    panelSize: string;
    panelVisible: boolean;
    startDateStr: string;
    endDateStr: string;
    selectedReportId: string;
    selectedChartId: string;
    startEndDateDisabled: boolean;
    extraParameters: ITaskDataRow;
}

export class EventPageReportFilterPanel extends React.Component<IEventPageReportFilterPanelProp, IEventPageReportFilterPanelState> {
    private orgStateHash: string = "";

    getReportByChartItem = (chartItem: IChartItem, generalFilter: any, reportFilter: any): IReportItem => {
        const result = ReportItem.createReportItem(chartItem?.id, "", ReportType.Pdf, chartItem?.taskType);
        result.taskDataRow = chartItem?.taskDataRow;
        result.generalFilter = generalFilter;
        result.reportFilter = reportFilter;
        result.copyReportParameterValuesFromSavedValues();
        return result;
    };

    getStateFromProps = (props: IEventPageReportFilterPanelProp): IEventPageReportFilterPanelState => {
        const selectedReportId = props.chartPanelDrawerMode ? props.chartPanel?.reportId : "";
        const chartItem = props.chartPanelDrawerMode ? props.chartItems.find(i => i.reportName === selectedReportId) : null;
        let taskType = props.report?.taskType;
        let taskDataRow = props.report?.taskDataRow;
        if (props.chartPanelDrawerMode) {
            const report = this.getReportByChartItem(chartItem, props.chartPanel?.generalFilter, props.chartPanel?.reportFilter);
            taskType = report.taskType;
            taskDataRow = report.taskDataRow;
        }
        const dynamicTimeFilters = props.chartPanelDrawerMode
            ? props.dynamicTimeFilters.filter(i => i.id !== "0")
            : props.dynamicTimeFilters;
        return {
            dynamicTimeFilters,
            taskType,
            taskDataRow,
            name: props.chartPanelDrawerMode ? props.chartPanel?.reportTitle : "",
            panelSize: ((props.chartPanelDrawerMode ? props.panelSize : null) ?? FrontPagePanelSize.OneXOne).toString(10),
            panelVisible: (props.chartPanelDrawerMode ? props.panelVisible : null) ?? false,
            startDateStr: props.selectedStartDate ? Base.timeToDateStr(props.selectedStartDate) : "",
            endDateStr: props.selectedEndDate ? Base.timeToDateStr(props.selectedEndDate) : "",
            selectedReportId,
            selectedChartId: props.chartPanelDrawerMode ? props.chartPanel?.chartId : "",
            charts: props.chartPanelDrawerMode ? chartItem?.chartNames.map(i => IdTitle.createIdTitle(i, i)) : [],
            selectedUnitIds: props.selectedUnitIds?.slice(0) || [],
            selectedUrgencyClassIds: props.selectedUrgencyClassIds.slice(0) || [],
            selectedStateIds: props.selectedStateIds?.slice(0) || [],
            selectedEventTypeIds: props.selectedEventTypeIds?.slice(0) || [],
            selectedEventTypeBIds: props.selectedEventTypeBIds?.slice(0) || [],
            selectedEventTypeCIds: props.selectedEventTypeCIds?.slice(0) || [],
            selectedPartnerCompanyIds: props.selectedPartnerCompanyIds?.slice(0) || [],
            selectedCreateUserCodes: props.selectedCreateUserCodes?.slice(0) || [],
            selectedHandlerUserCodes: props.selectedHandlerUserCodes?.slice(0) || [],
            selectedObservers: props.selectedObservers?.slice(0) || [],
            selectedDynamicTimeFilterId: props.selectedDynamicTimeFilterId,
            startEndDateDisabled: props.selectedDynamicTimeFilterId !== "0",
            extraParameters: taskType && taskDataRow ? new TaskDataRow(taskDataRow) : null,
        };
    };

    constructor(props: IEventPageReportFilterPanelProp) {
        super(props);
        this.state = this.getStateFromProps(props);
        this.orgStateHash = this.getSaveDataFromState(props, this.state).hash;
    }

    componentDidUpdate(prevProps: IEventPageReportFilterPanelProp, prevState: IEventPageReportFilterPanelState): void {
        const props = this.props;
        if (!prevProps || prevProps.report?.getHash() === props.report?.getHash()) return;
        const state = this.getStateFromProps(props);
        this.setState(state);
        this.orgStateHash = this.getSaveDataFromState(props, state).hash;
    }

    getGeneralFilterFromState = (): IEventGeneralFilter => {
        const state = this.state;
        const selectedStartDate = state.startDateStr ? state.startDateStr.toDate().getTime() : null;
        const selectedEndDate = state.endDateStr ? state.endDateStr.toDate().getTime() : null;
        return getEventGeneralFilterFromFilterState({
            selectedStartDate,
            selectedEndDate,
            selectedUnitIds: state.selectedUnitIds,
            selectedStateIds: state.selectedStateIds,
            selectedUrgencyClassIds: state.selectedUrgencyClassIds,
            selectedEventTypeIds: state.selectedEventTypeIds,
            selectedEventTypeBIds: state.selectedEventTypeBIds,
            selectedEventTypeCIds: state.selectedEventTypeCIds,
            selectedPartnerCompanyIds: state.selectedPartnerCompanyIds,
            selectedCreateUserCodes: state.selectedCreateUserCodes,
            selectedHandlerUserCodes: state.selectedHandlerUserCodes,
            selectedObservers: state.selectedObservers,
            selectedDynamicTimeFilterId: state.selectedDynamicTimeFilterId
        });
    };

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "name") {
            this.setState({ name: value });
        }
    };

    handleStartDateChange = (value: string) => {
        this.setState({ startDateStr: value });
    };

    handleEndDateChange = (value: string) => {
        this.setState({ endDateStr: value });
    };

    handleSelectedChartIdChange = (value: string) => {
        const props = this.props;
        const chartItem = props.chartItems.find(i => i.reportName === value);
        if (!chartItem) return;
        const charts = chartItem?.chartNames.map(i => IdTitle.createIdTitle(i, i));
        const report = this.getReportByChartItem(chartItem, this.getGeneralFilterFromState(), SaveReportDialog.getReportFilterFromTaskType(chartItem?.taskType, this.state.extraParameters));
        this.setState({
            taskType: report.taskType,
            taskDataRow: report.taskDataRow,
            selectedReportId: value,
            charts,
            selectedChartId: charts[0].id
        });
    };

    handleSelectedDynamicTimeFilterIdChange = (value: string) => {
        if (value.toInteger() > 0) {
            this.setState({ selectedDynamicTimeFilterId: value, startDateStr: "", endDateStr: "", startEndDateDisabled: true });
        } else {
            this.setState({ selectedDynamicTimeFilterId: value, startEndDateDisabled: false });
        };
    };

    handleRefreshParameters = (taskDataRowCols: ITaskDataRowCol[]) => {
        const state = this.state;
        if (!state.extraParameters) return;
        const extraParameters = new TaskDataRow(state.extraParameters);
        extraParameters.cols = taskDataRowCols;
        this.setState({ extraParameters });
    };

    getSaveDataFromState = (props: IEventPageReportFilterPanelProp, state: IEventPageReportFilterPanelState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("name", state.name);
        result.append("selectedReportId", state.selectedReportId);
        result.append("selectedChartId", state.selectedChartId);
        result.append("panelSize", state.panelSize);
        result.append("panelVisible", state.panelVisible);
        result.append("startDate", state.startDateStr);
        result.append("endDate", state.endDateStr);
        result.append("selectedStateIds", state.selectedStateIds);
        result.append("selectedUnitIds", state.selectedUnitIds);
        result.append("selectedUrgencyClassIds", state.selectedUrgencyClassIds);
        result.append("selectedEventTypeIds", state.selectedEventTypeIds);
        result.append("selectedEventTypeBIds", state.selectedEventTypeBIds);
        result.append("selectedEventTypeCIds", state.selectedEventTypeCIds);
        result.append("selectedPartnerCompanyIds", state.selectedPartnerCompanyIds);
        result.append("selectedCreateUserCodes", state.selectedCreateUserCodes);
        result.append("selectedHandlerUserCodes", state.selectedHandlerUserCodes);
        result.append("selectedObservers", state.selectedObservers);
        result.append("selectedDynamicTimeFilterId", state.selectedDynamicTimeFilterId ? state.selectedDynamicTimeFilterId : null);
        if (state.taskType && state.taskDataRow) {
            result.append("parameters", state.extraParameters.cols.map(i => state.extraParameters.getTaskDataRowColUiValue(i.field, state.taskType)));
        }
        return result;
    };
    
    checkErrors = (): Promise<boolean> => {
        const props = this.props;
        const state = this.state;
        return new Promise<boolean>((resolve) => {
            if (props.chartPanelDrawerMode) {
                if (!state.name) {
                    AppUtils.showErrorMessage(Translations.NameMustBeDefined);
                    return resolve(false);
                }
                if (props.reservedChartNames.find(i => i.equalIgnoreCase(state.name))) {
                    AppUtils.showErrorMessage(Translations.NameIsAlreadyInUse);
                    return resolve(false);
                }
            }
            return resolve(true);
        });
    };

    handleSaveReport = async (saveAs: boolean) => {
        const props = this.props;
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return false;
        const selectedStartDate = state.startDateStr ? state.startDateStr.toDate().getTime() : null;
        const selectedEndDate = state.endDateStr ? state.endDateStr.toDate().getTime() : null;
        if (!saveAs) {
            props.onOk(cloneEventPageFilterState({ ...state, selectedStartDate, selectedEndDate }), state.extraParameters);
            await this.props.onSaveReport(false, null);
        } else {
            await this.props.onSaveReport(true, () => {
                return props.onOk(cloneEventPageFilterState({ ...state, selectedStartDate, selectedEndDate }), state.extraParameters);
            });
        }
    };

    handleClear = () => {
        const props = this.props;
        const state = this.state;
        const extraParameters = state.extraParameters ? new TaskDataRow(state.extraParameters) : null;
        if (extraParameters) {
            extraParameters.initializeToTaskType(state.taskType);
        }
        this.setState({
            startDateStr: "",
            endDateStr: "",
            selectedUnitIds: [props.units[0].id],
            selectedStateIds: [props.states[0].id],
            selectedUrgencyClassIds: [props.urgencyClasses[0].id],
            selectedEventTypeIds: [props.eventTypes[0].id],
            selectedEventTypeBIds: [props.eventTypeBs[0].id],
            selectedEventTypeCIds: [props.eventTypeCs[0].id],
            selectedPartnerCompanyIds: [props.partners[0].id],
            selectedCreateUserCodes: [props.creators[0].id],
            selectedHandlerUserCodes: [props.handlers[0].id],
            selectedObservers: [props.observers[0].id],
            selectedDynamicTimeFilterId: state.dynamicTimeFilters[0].id,
            extraParameters
        });
    };

    // #region ChartPanel
    saveChartPanel = async () => {
        const props = this.props;
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return;
        const chartItem = props.chartItems.find(i => i.reportName === state.selectedReportId);
        if (!chartItem) return;
        //Update chartPanel
        const selectedStartDate = state.startDateStr ? state.startDateStr.toDate().getTime() : null;
        const selectedEndDate = state.endDateStr ? state.endDateStr.toDate().getTime() : null;
        const chartPanel = new SavedChartPanel(props.chartPanel);
        chartPanel.id = state.name;
        chartPanel.reportTitle = state.name;
        chartPanel.size = state.panelSize.toInteger();
        chartPanel.visible = state.panelVisible;
        chartPanel.reportId = state.selectedReportId;
        chartPanel.chartId = state.selectedChartId;
        chartPanel.generalFilter = getEventGeneralFilterFromFilterState({
            ...state,
            selectedStartDate,
            selectedEndDate
        });
        chartPanel.reportFilter = SaveReportDialog.getReportFilterFromTaskType(chartItem?.taskType, state.extraParameters);
        //Set SaveData
        const saveData = new SaveDataObj();
        saveData.append("applicationPermissionType", ApplicationPermissionType.Event);
        saveData.append("orgId", props.chartPanel?.id);
        saveData.append("item", chartPanel);
        //Call server
        const result = await AppUtils.callService(() => chartPanelService.saveChartPanel(saveData.obj));
        if (!result) return null;
        props.onChartPanelSaved(chartPanel);
    };

    handleRemoveChartPanel = async () => {
        const props = this.props;
        if (props.chartPanel?.isNew()) return;
        const confirmationResult = await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveChartPanelParameter, props.chartPanel?.reportTitle));
        if (confirmationResult !== ConfirmationDialogResult.Yes) return;
        const saveData = new SaveDataObj();
        saveData.append("applicationPermissionType", ApplicationPermissionType.Event);
        saveData.append("id", props.chartPanel?.id);
        const result = await AppUtils.callService(() => chartPanelService.removeChartPanel(saveData.obj), false);
        if (!result) return;
        props.onChartPanelRemoved(props.chartPanel?.id);
    };
    // #end region ChartPanel

    handleSave = async () => {
        const props = this.props;
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return false;
        if (props.onOk) {
            const selectedStartDate = state.startDateStr ? state.startDateStr.toDate().getTime() : null;
            const selectedEndDate = state.endDateStr ? state.endDateStr.toDate().getTime() : null;
            props.onOk(cloneEventPageFilterState({ ...state, selectedStartDate, selectedEndDate }), state.extraParameters);
        } else if (props.onChartPanelSaved) {
            this.saveChartPanel();
        }
    };

    handleCancel = async (checkChanges: boolean) => {
        AppUtils.cancelEditDialog(checkChanges, this.getSaveDataFromState(this.props, this.state).hash, this.orgStateHash, this.handleSave, this.props.onCancel, Translations.YouHaveUnapprovedChangesDoYouWantApproveChanges);
    };

    render() {
        const props = this.props;
        const state = this.state;

        const content = <>
            {!!props.onSaveReport && !!props.onRemoveReport &&
                <FilterSearchField
                    filter={props.report}
                    canRemoveSharedFilters={props.canRemoveSharedReports}
                    isReport={true}
                    onSaveFilter={this.handleSaveReport}
                    onRemoveFilter={props.onRemoveReport}
                />
            }
            {!!props.chartPanelDrawerMode &&
                <>
                    <GridRow>
                        <Grid item sm={12}>
                            <FormField title={Translations.PanelName} required margin={0}>
                                <TextField type="text" name="name" title={Translations.PanelName} inputProps={{ maxLength: 50 }} value={state.name} required={true} onChange={this.handleChange} />
                            </FormField>
                        </Grid>
                    </GridRow>
                    <GridRow sx={{ alignItems: "flex-end" }}>
                        <Grid item sm={6}>
                            <FormField title={""}>
                                <FormControlLabel
                                    label={Translations.PanelVisible}
                                    control={
                                        <Checkbox
                                            checked={state.panelVisible}
                                            onChange={(_e, panelVisible) => this.setState({ panelVisible })}
                                        />
                                    }
                                />
                            </FormField>
                        </Grid>
                        <Grid item sm={6}>
                            <FormField title={Translations.PanelSize}>
                                <IdTitleDropdown
                                    selectedId={state.panelSize}
                                    items={[
                                        { id: "1", title: Translations.PanelSize1 },
                                        { id: "2", title: Translations.PanelSize2 },
                                        { id: "5", title: Translations.PanelSize5 },
                                        { id: "3", title: Translations.PanelSize3 },
                                        { id: "4", title: Translations.PanelSize4 },
                                        { id: "6", title: Translations.PanelSize6 }
                                    ]}
                                    onChange={(panelSize) => this.setState({ panelSize })}
                                />
                            </FormField>
                        </Grid>
                    </GridRow>
                    <GridRow>
                        <Grid item sm={6}>
                            <FormField title={Translations.Report}>
                                <IdTitleDropdown
                                    selectedId={state.selectedReportId}
                                    items={(props.chartItems ?? []).map(i => {
                                        return { id: i?.reportName, title: i?.reportDescription };
                                    })}
                                    onChange={this.handleSelectedChartIdChange}
                                />
                            </FormField>
                        </Grid>
                        <Grid item sm={6}>
                            <FormField title={Translations.ChartType}>
                                <IdTitleDropdown
                                    selectedId={state.selectedChartId}
                                    items={(state.charts ?? []).map(i => {
                                        return { id: i.id, title: i.title };
                                    })}
                                    onChange={(selectedChartId) => this.setState({ selectedChartId })}
                                />
                            </FormField>
                        </Grid>
                    </GridRow>
                </>
            }
            {!!state.extraParameters &&
                <GridRow sx={{ margin: "1rem -1rem -1rem -1rem" }}>
                    <TaskDynamicForm
                        taskTypeCols={state.taskType?.formCols}
                        taskDataRowCols={state.extraParameters?.cols}
                        onRefreshCols={this.handleRefreshParameters}
                    />
                </GridRow>
            }
            <FormSectionTitle>{Translations.Event}</FormSectionTitle>
            {!props.chartPanelDrawerMode &&
                <FormField title={Translations.Date} margin={0}>
                    <GridRow>
                        <Grid item sm={6}>
                            {!state.startEndDateDisabled &&
                                <DatePicker
                                    value={state.startDateStr}
                                    onChange={this.handleStartDateChange}
                                />
                            }
                            {state.startEndDateDisabled &&
                                <TextField type="text" title={Translations.Date} disabled={true} />
                            }
                        </Grid>
                        <Grid item sm={6}>
                            {!state.startEndDateDisabled &&
                                <DatePicker
                                    value={state.endDateStr}
                                    onChange={this.handleEndDateChange}
                                />
                            }
                            {state.startEndDateDisabled &&
                                <TextField type="text" title={Translations.Date} disabled={true} />
                            }
                        </Grid>
                    </GridRow>
                    <GridRow>
                        <Grid item sm={12}>
                            <FormField title="">
                                <IdTitleDropdown
                                    selectedId={state.selectedDynamicTimeFilterId}
                                    items={state.dynamicTimeFilters}
                                    onChange={this.handleSelectedDynamicTimeFilterIdChange}
                                />
                            </FormField>
                        </Grid>
                    </GridRow>
                </FormField>
            }
            {props.chartPanelDrawerMode &&
                <GridRow>
                    <Grid item sm={12}>
                        <FormField title={Translations.Date} margin={0}>
                            <IdTitleDropdown
                                selectedId={state.selectedDynamicTimeFilterId}
                                items={state.dynamicTimeFilters}
                                onChange={this.handleSelectedDynamicTimeFilterIdChange}
                            />
                        </FormField>
                    </Grid>
                </GridRow>
            }
            <GridRow>
                <Grid item sm={12}>
                    <FormField title={Translations.Unit}>
                        <TreeCheckboxDropdown
                            required={true}
                            selectedIds={state.selectedUnitIds}
                            values={props.units || []}
                            onChange={(selectedUnitIds: string[]) => this.setState({ selectedUnitIds })}
                        />
                    </FormField>
                </Grid>
            </GridRow>
            <GridRow>
                <Grid item sm={6}>
                    <FormField title={Translations.State}>
                        <CheckboxDropdown
                            required={true}
                            selectedIds={state.selectedStateIds}
                            toggleAll={true}
                            toggleAllId={EventStateFilter.All.toString(10)}
                            values={props.states || []}
                            onChange={(selectedStateIds: string[]) => this.setState({ selectedStateIds })}
                        />
                    </FormField>
                </Grid>
                <Grid item sm={6}>
                    <FormField title={Translations.EventUrgencyClass}>
                        <CheckboxDropdown
                            required={true}
                            selectedIds={state.selectedUrgencyClassIds}
                            toggleAll={true}
                            toggleAllId={Base.emptyGuid}
                            values={props.urgencyClasses || []}
                            onChange={(selectedUrgencyClassIds: string[]) => this.setState({ selectedUrgencyClassIds })}
                        />
                    </FormField>
                </Grid>
            </GridRow>
            {!props.chartPanelDrawerMode &&
                <GridRow>
                    <Grid item sm={6}>
                        <FormField title={Translations.Partner}>
                            <CheckboxDropdown
                                required={true}
                                selectedIds={state.selectedPartnerCompanyIds}
                                toggleAll={true}
                                toggleAllId={Base.emptyGuid}
                                values={props.partners || []}
                                onChange={(selectedPartnerCompanyIds: string[]) => this.setState({ selectedPartnerCompanyIds })}
                            />
                        </FormField>
                    </Grid>
                    <Grid item sm={6}>
                        <FormField title={Translations.EventCreator}>
                            <CheckboxDropdown
                                required={true}
                                selectedIds={state.selectedCreateUserCodes}
                                toggleAll={true}
                                toggleAllId={""}
                                values={props.creators || []}
                                onChange={(selectedCreateUserCodes: string[]) => this.setState({ selectedCreateUserCodes })}
                            />
                        </FormField>
                    </Grid>
                </GridRow>
            }
            {!props.chartPanelDrawerMode &&
                <GridRow>
                    <Grid item sm={6}>
                        <FormField title={Translations.EventHandler}>
                            <CheckboxDropdown
                                required={true}
                                selectedIds={state.selectedHandlerUserCodes}
                                toggleAll={true}
                                toggleAllId={""}
                                values={props.handlers || []}
                                onChange={(selectedHandlerUserCodes: string[]) => this.setState({ selectedHandlerUserCodes })}
                            />
                        </FormField>
                    </Grid>
                    <Grid item sm={6}>
                        <FormField title={Translations.Observer}>
                            <CheckboxDropdown
                                required={true}
                                selectedIds={state.selectedObservers}
                                toggleAll={true}
                                toggleAllId={Base.emptyGuid}
                                values={props.observers || []}
                                onChange={(selectedObservers: string[]) => this.setState({ selectedObservers })}
                            />
                        </FormField>
                    </Grid>
                </GridRow>
            }
            <FormSectionTitle>{Translations.Identification}</FormSectionTitle>
            <GridRow>
                <Grid item sm={6}>
                    <FormField title={Translations.EventType} margin={0}>
                        <TreeCheckboxDropdown
                            required={true}
                            toggleNotSetId={EventTypeNotSetId}
                            selectedIds={state.selectedEventTypeIds}
                            values={props.eventTypes || []}
                            onChange={(selectedEventTypeIds: string[]) => this.setState({ selectedEventTypeIds })}
                        />
                    </FormField>
                </Grid>
                <Grid item sm={6}>
                    <FormField title={Translations.EventTypeB} margin={0}>
                        <TreeCheckboxDropdown
                            required={true}
                            toggleNotSetId={EventTypeNotSetId}
                            selectedIds={state.selectedEventTypeBIds}
                            values={props.eventTypeBs || []}
                            onChange={(selectedEventTypeBIds: string[]) => this.setState({ selectedEventTypeBIds })}
                        />
                    </FormField>
                </Grid>
            </GridRow>
            <GridRow>
                <Grid item sm={6}>
                    <FormField title={Translations.EventTypeC}>
                        <TreeCheckboxDropdown
                            required={true}
                            toggleNotSetId={EventTypeNotSetId}
                            selectedIds={state.selectedEventTypeCIds}
                            values={props.eventTypeCs || []}
                            onChange={(selectedEventTypeCIds: string[]) => this.setState({ selectedEventTypeCIds })}
                        />
                    </FormField>
                </Grid>
            </GridRow>
        </>;

        if (props.chartPanelDrawerMode) {
            return (
                <PageChartFilterDrawer
                    title={props.chartPanel.reportTitle}
                    buttons={
                        <FilterButtons
                            chartPanelPropertiesMode
                            marginTop="0"
                            onCancel={this.handleCancel}
                            onClear={this.handleClear}
                            onRemove={this.handleRemoveChartPanel}
                            onSave={this.handleSave}
                        />
                    }
                    onClose={() => this.handleCancel(false)}
                >
                    {content}
                </PageChartFilterDrawer>
            );
        }

        return (
            <Box width="100%" sx={{ paddingTop: "1rem" }}>
                {content}
                <FilterButtons
                    chartPanelPropertiesMode={false}
                    onCancel={undefined}
                    onClear={this.handleClear}
                    onSave={this.handleSave}
                />
            </Box>
        );
    }
}